import React, { useState, useRef } from 'react'
import { useTranslation } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { Button } from '@/components/ui/button'

const JobApplicationForm = ({ jobTitle = '', jobs = [] }) => {
  const t = useTranslation()
  const { language } = useLanguage()
  const formRef = useRef(null)
  const [formData, setFormData] = useState({
    name: '',
    position: jobTitle,
    phone: '',
    email: '',
    message: ''
  })
  const [resume, setResume] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null) // 'success', 'error', or null
  const [captchaValue, setCaptchaValue] = useState('')
  const [captchaQuestion, setCaptchaQuestion] = useState('')
  const [captchaAnswer, setCaptchaAnswer] = useState('')

  // Generate simple math captcha
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1
    const num2 = Math.floor(Math.random() * 10) + 1
    const question = `${num1} + ${num2} = ?`
    const answer = (num1 + num2).toString()
    setCaptchaQuestion(question)
    setCaptchaAnswer(answer)
    setCaptchaValue('')
  }

  // Initialize captcha on component mount
  React.useEffect(() => {
    generateCaptcha()
  }, [])

  // Update position when jobTitle prop changes
  React.useEffect(() => {
    if (jobTitle) {
      setFormData(prev => ({ ...prev, position: jobTitle }))
    }
  }, [jobTitle])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert(t('careers.form.fileSizeError'))
        return
      }
      // Check file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
      if (!allowedTypes.includes(file.type)) {
        alert(t('careers.form.fileTypeError'))
        return
      }
      setResume(file)
    }
  }

  const handleCaptchaChange = (e) => {
    setCaptchaValue(e.target.value)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validate captcha
    if (captchaValue !== captchaAnswer) {
      alert(t('careers.form.captchaError'))
      generateCaptcha()
      return
    }

    // Validate position is selected
    if (!formData.position || formData.position.trim() === '') {
      alert(t('careers.form.positionRequired', 'Please select a position'))
      return
    }

    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      // Prepare form data for submission
      const formDataToSend = new FormData()
      formDataToSend.append('name', formData.name)
      formDataToSend.append('email', formData.email)
      formDataToSend.append('phone', formData.phone || '')
      formDataToSend.append('position', formData.position || 'General Application')
      formDataToSend.append('message', formData.message || '')
      formDataToSend.append('language', language)
      
      // Add resume file if provided
      if (resume) {
        formDataToSend.append('resume', resume)
      }

      // Submit to our Cloudflare Pages Function
      // Vite proxy will forward /api requests to wrangler dev server
      // In production, this will work with Cloudflare Pages Functions
      const response = await fetch('/api/careers/apply', {
        method: 'POST',
        body: formDataToSend
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('API error:', response.status, errorText)
        setSubmitStatus('error')
        return
      }

      const result = await response.json()

      if (result.success) {
        setSubmitStatus('success')
        // Reset form
        setFormData({
          name: '',
          position: jobTitle || '',
          phone: '',
          email: '',
          message: ''
        })
        setResume(null)
        if (formRef.current) {
          formRef.current.reset()
        }
        generateCaptcha()
      } else {
        // Log the actual error from the API
        console.error('Form submission error:', result)
        setSubmitStatus('error')
      }
    } catch (error) {
      console.error('Form submission error:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="max-w-2xl mx-auto">
      {/* Success Message */}
      {submitStatus === 'success' && (
        <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                {t('careers.form.success.title')}
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>{t('careers.form.success.message')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {submitStatus === 'error' && (
        <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                {t('careers.form.error.title')}
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{t('careers.form.error.message')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            {t('careers.form.name')} *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-darkGreen focus:border-transparent"
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            {t('careers.form.email')} *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-darkGreen focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
            {t('careers.form.phone')}
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-darkGreen focus:border-transparent"
          />
        </div>
        <div>
          <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-2">
            {t('careers.form.position')} *
          </label>
          {jobs.length > 0 ? (
            <select
              id="position"
              name="position"
              value={formData.position}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-darkGreen focus:border-transparent bg-white"
            >
              <option value="">{t('careers.form.selectPosition')}</option>
              {jobs.map((job) => (
                <option key={job.slug} value={job.title || job.slug}>
                  {job.title || job.slug}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              id="position"
              name="position"
              value={formData.position}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-darkGreen focus:border-transparent"
            />
          )}
        </div>
      </div>

      <div className="mb-6">
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
          {t('careers.form.message')}
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleInputChange}
          rows={6}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-darkGreen focus:border-transparent"
          placeholder={t('careers.form.messagePlaceholder')}
        ></textarea>
      </div>

      <div className="mb-6">
        <label htmlFor="resume" className="block text-sm font-medium text-gray-700 mb-2">
          {t('careers.form.resume')}
        </label>
        <div className="flex items-center gap-4">
          <input
            type="file"
            id="resume"
            name="resume"
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx"
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-darkGreen file:text-white hover:file:bg-primary-darkGreen/90"
          />
          {resume && (
            <span className="text-sm text-gray-600">
              {resume.name}
            </span>
          )}
        </div>
        <p className="mt-2 text-xs text-gray-500">
          {t('careers.form.resumeFormats')}
        </p>
      </div>

      {/* Captcha */}
      <div className="mb-6">
        <label htmlFor="captcha" className="block text-sm font-medium text-gray-700 mb-2">
          {t('careers.form.securityCheck')} {captchaQuestion}
        </label>
        <div className="flex items-center space-x-4">
          <input
            type="text"
            id="captcha"
            value={captchaValue}
            onChange={handleCaptchaChange}
            required
            className="w-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-darkGreen focus:border-transparent"
            placeholder={t('careers.form.answer')}
          />
          <button
            type="button"
            onClick={generateCaptcha}
            className="px-4 py-2 text-sm text-primary-darkGreen hover:text-primary-darkGreen/80 underline"
          >
            {t('careers.form.newQuestion')}
          </button>
        </div>
      </div>

      <div className="text-center">
        <Button type="submit" variant="brand" disabled={isSubmitting}>
          {isSubmitting ? t('careers.form.sending') : t('careers.form.send')}
        </Button>
      </div>
    </form>
  )
}

export default JobApplicationForm

